/**
 * File Upload Utility
 *
 * Mode-aware file upload that works in both direct and control_plane modes.
 */

import { useAuthStore } from "@/lib/store/authStore";
import { tenantServer } from "@/lib/api/tenantServerClient";
import { getFrappeClient as getFrappeClientFromCore } from "@/lib/frappe-client";

export interface UploadFileOptions {
  doctype?: string;
  docname?: string;
  fieldname?: string;
  isPrivate?: boolean;
  folder?: string;
}

export interface UploadFileResult {
  file_url: string;
  file_name: string;
}

/**
 * Upload a file to Frappe
 *
 * For Phase 1: Allows upload before doc save (unattached files)
 * File URL is stored in field, doc save will keep that URL
 *
 * In control_plane mode: Uses JWT-authenticated tenantServer.uploadFile
 * In direct mode: Uses session-based Frappe upload with CSRF token
 *
 * @param file - File to upload
 * @param options - Upload options
 * @returns File URL and name
 * @throws Error if upload fails (size, type, server error, etc.)
 */
export async function uploadFile(
  file: File,
  options: UploadFileOptions = {}
): Promise<UploadFileResult> {
  const authMode = useAuthStore.getState().authMode;

  // Control Plane mode - use JWT-authenticated upload
  if (authMode === 'control_plane') {
    const result = await tenantServer.uploadFile(file, {
      doctype: options.doctype,
      docname: options.docname,
      fieldname: options.fieldname,
      isPrivate: options.isPrivate,
      folder: options.folder,
    });
    return {
      file_url: result.file_url,
      file_name: result.file_name,
    };
  }

  // Direct mode - use session-based Frappe upload
  return uploadFileDirectMode(file, options);
}

/**
 * Direct mode file upload using session auth + CSRF token
 */
async function uploadFileDirectMode(
  file: File,
  options: UploadFileOptions
): Promise<UploadFileResult> {
  const client = getFrappeClientFromCore();
  const config = (client as any).config as { url: string } | undefined;
  const baseUrl = config?.url || "/api";
  const apiPrefix = baseUrl === "/api" ? "" : "/api";
  const url = `${baseUrl}${apiPrefix}/method/upload_file`;

  // Build FormData
  const formData = new FormData();
  formData.append("file", file);

  if (options.isPrivate != null) {
    formData.append("is_private", options.isPrivate ? "1" : "0");
  }
  if (options.doctype) {
    formData.append("doctype", options.doctype);
  }
  if (options.docname) {
    formData.append("docname", options.docname);
  }
  if (options.fieldname) {
    formData.append("fieldname", options.fieldname);
  }
  if (options.folder) {
    formData.append("folder", options.folder);
  }

  // Get auth headers (includes CSRF token)
  // Use the client's internal getAuthHeaders method
  const getAuthHeaders = (client as any).getAuthHeaders as (() => Promise<HeadersInit>) | undefined;
  const authHeaders = getAuthHeaders ? await getAuthHeaders() : {};

  // Remove Content-Type header to let browser set it with boundary for FormData
  const headersRecord = authHeaders as Record<string, string>;
  const { "Content-Type": _, ...headersWithoutContentType } = headersRecord;

  const response = await fetch(url, {
    method: "POST",
    headers: headersWithoutContentType,
    credentials: "include",
    body: formData,
  });

  // Handle non-JSON responses gracefully
  let data: any;
  try {
    data = await response.json();
  } catch (jsonError) {
    // Server returned non-JSON (HTML error, reverse proxy, etc.)
    if (!response.ok) {
      throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
    }
    throw new Error("Unexpected file upload response (non-JSON)");
  }

  if (!response.ok) {
    const errorMessage = data?.message || data?.exc || `Upload failed: ${response.statusText}`;
    throw new Error(errorMessage);
  }

  // Validate response shape
  if (!data || !data.message || !data.message.file_url) {
    throw new Error("Unexpected file upload response");
  }

  return {
    file_url: data.message.file_url,
    file_name: data.message.file_name || file.name,
  };
}
